import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { colorApi, modelApi, brandApi } from '../api/client';
import type { AdminModelWithColors, AdminColorItem, ColorCode, ColorStatus } from '@bosamie/shared';
import { COLOR_SLOTS, buildSlotGrid, findNextSlot, isSlotsFull } from '@bosamie/shared';
import {
  showToast, ConfirmDialog, Drawer, StatusBadge, ImageUploader,
} from '../components/ui';

export function Colors() {
  const { brandId, modelId } = useParams<{ brandId: string; modelId: string }>();
  const [data, setData] = useState<AdminModelWithColors | null>(null);
  const [brandName, setBrandName] = useState('');
  const [loading, setLoading] = useState(true);

  // Drawer states
  const [createOpen, setCreateOpen] = useState(false);
  const [editColor, setEditColor] = useState<AdminColorItem | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminColorItem | null>(null);

  // Create form
  const [createCover, setCreateCover] = useState('');
  const [createDetails, setCreateDetails] = useState<string[]>([]);

  // Edit form
  const [editStatus, setEditStatus] = useState<ColorStatus>('sale');
  const [editCover, setEditCover] = useState('');
  const [editDetails, setEditDetails] = useState<string[]>([]);

  const fetch = useCallback(async () => {
    if (!brandId || !modelId) return;
    setLoading(true);
    try {
      const [brands, modelData] = await Promise.all([
        brandApi.list(),
        colorApi.getWithColors(brandId, modelId),
      ]);
      const b = brands.find((x) => x.id === brandId);
      if (b) setBrandName(b.name);
      setData(modelData);
    } catch {
      showToast('加载失败');
    } finally {
      setLoading(false);
    }
  }, [brandId, modelId]);

  useEffect(() => { fetch(); }, [fetch]);

  const slots = data ? buildSlotGrid(data.colors) : [];
  const full = data ? isSlotsFull(data.colors) : false;

  const openCreate = () => {
    if (full) {
      showToast('颜色已填满');
      return;
    }
    setCreateCover('');
    setCreateDetails([]);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!brandId || !modelId) return;
    if (!createCover) {
      showToast('请上传首图');
      return;
    }
    try {
      await colorApi.create(brandId, modelId, {
        coverUrl: createCover,
        detailUrls: createDetails,
      });
      showToast('创建成功');
      setCreateOpen(false);
      fetch();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '创建失败');
    }
  };

  const openEdit = (color: AdminColorItem) => {
    setEditColor(color);
    setEditStatus(color.status);
    setEditCover(color.coverUrl || '');
    setEditDetails(color.detailUrls);
  };

  const handleUpdateStatus = async () => {
    if (!editColor) return;
    if (editStatus === 'sale' && !editCover) {
      showToast('在售状态需要首图');
      return;
    }
    try {
      await colorApi.updateStatus(editColor.id, editStatus);
      showToast('状态已更新');
      fetch();
      setEditColor(null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '更新失败');
    }
  };

  const handleUpdateCover = async () => {
    if (!editColor || !editCover) return;
    try {
      await colorApi.updateCover(editColor.id, editCover);
      showToast('首图已更新');
      fetch();
    } catch {
      showToast('更新失败');
    }
  };

  const handleUpdateDetails = async () => {
    if (!editColor || editDetails.length === 0) {
      showToast('请至少选择一张细节图');
      return;
    }
    try {
      await colorApi.updateDetails(editColor.id, editDetails);
      showToast('细节图已更新');
      fetch();
    } catch {
      showToast('更新失败');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await colorApi.delete(deleteTarget.id);
      showToast('删除成功');
      setDeleteTarget(null);
      setEditColor(null);
      fetch();
    } catch {
      showToast('删除失败');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="skel" style={{ aspectRatio: 1, borderRadius: 8 }} />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <div>加载失败</div>;

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">
          {brandName} · {data.code} · 颜色管理
        </div>
        <button
          className="btn btn-primary"
          onClick={openCreate}
          disabled={full}
          title={full ? '颜色已填满' : ''}
        >
          + 添加颜色
        </button>
      </div>

      {/* A-F Grid */}
      <div className="color-grid-admin">
        {slots.map((slot) => {
          const color = slot.color;
          if (!color) {
            return (
              <div
                key={slot.code}
                className="color-cell-admin empty"
                onClick={openCreate}
              >
                <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--ink-3)' }}>{slot.code}</span>
                <span>＋ 添加</span>
              </div>
            );
          }
          return (
            <div
              key={slot.code}
              className={`color-cell-admin ${color.status}`}
              onClick={() => openEdit(color)}
            >
              {color.coverUrl && <img src={color.coverUrl} alt={`Color ${color.code}`} />}
              <div className="cell-label">{color.code}</div>
              <div className="cell-badge">
                <StatusBadge status={color.status} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Drawer */}
      {createOpen && (
        <Drawer
          title="新增颜色"
          onClose={() => setCreateOpen(false)}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setCreateOpen(false)}>取消</button>
              <button className="btn btn-primary" onClick={handleCreate}>创建</button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">首图 <span className="req">*</span></label>
            <ImageUploader
              files={createCover ? [{ url: createCover }] : []}
              onAdd={(url) => setCreateCover(url)}
              onRemove={() => setCreateCover('')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">细节图（选填）</label>
            <ImageUploader
              multiple
              files={createDetails.map((url) => ({ url }))}
              onAdd={(url) => setCreateDetails((prev) => [...prev, url])}
              onRemove={(i) => setCreateDetails((prev) => prev.filter((_, idx) => idx !== i))}
            />
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 12 }}>
            系统将自动分配第一个可用槽位（{findNextSlot(data.colors) || '已满'}）
          </div>
        </Drawer>
      )}

      {/* Edit Drawer */}
      {editColor && (
        <Drawer
          title={`颜色 ${editColor.code} · 详情`}
          onClose={() => setEditColor(null)}
          footer={
            <button
              className="btn btn-danger"
              onClick={() => setDeleteTarget(editColor)}
              style={{ marginRight: 'auto' }}
            >
              删除
            </button>
          }
        >
          {/* Preview */}
          {editCover && (
            <div style={{ marginBottom: 16, borderRadius: 8, overflow: 'hidden' }}>
              <img src={editCover} alt="" style={{ width: '100%' }} />
            </div>
          )}

          {/* Status */}
          <div className="form-group">
            <label className="form-label">状态</label>
            <select
              className="form-select"
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as ColorStatus)}
            >
              <option value="sale">在售</option>
              <option value="pending">待上架</option>
              <option value="hidden">已隐藏</option>
            </select>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={handleUpdateStatus}>
              更新状态
            </button>
          </div>

          {/* Cover */}
          <div className="form-group">
            <label className="form-label">重传首图</label>
            <ImageUploader
              files={editCover ? [{ url: editCover }] : []}
              onAdd={(url) => { setEditCover(url); }}
              onRemove={() => setEditCover('')}
            />
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={handleUpdateCover} disabled={!editCover}>
              保存首图
            </button>
          </div>

          {/* Details */}
          <div className="form-group">
            <label className="form-label">细节图</label>
            <ImageUploader
              multiple
              files={editDetails.map((url) => ({ url }))}
              onAdd={(url) => setEditDetails((prev) => [...prev, url])}
              onRemove={(i) => setEditDetails((prev) => prev.filter((_, idx) => idx !== i))}
            />
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={handleUpdateDetails} disabled={editDetails.length === 0}>
              保存细节图
            </button>
          </div>
        </Drawer>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="删除颜色"
          danger
          body={`确定要删除颜色 ${deleteTarget.code} 吗？删除后槽位将被释放，可以重新添加。`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
